import { useRef, useEffect } from 'react';

interface MemoRef {
  pageHeight: number;
  lockedProductCode: string;
}

/**
 * Ensures that when products are being prepended to the current list, the user
 * doesn't loose the visual landmark of last visible row, before clicking the
 * "Show Previous" button
 */
function useStickyProductsPrepend(firstProductCode: string | undefined, isFirstPage = false): void {
  if (!firstProductCode) return;
  const memo = useRef<MemoRef>({ pageHeight: 0, lockedProductCode: '' });

  useEffect(() => {
    if (window.history.scrollRestoration) {
      window.history.scrollRestoration = 'manual';
    }
    memo.current = {
      pageHeight: getPageHeight(),
      lockedProductCode: firstProductCode
    };
  }, []);

  useEffect(() => {
    const pageHeight = getPageHeight();

    if (shouldScroll()) {
      scrollToLockedProduct(pageHeight, memo.current.pageHeight, isFirstPage);
    }
    memo.current = {
      pageHeight,
      lockedProductCode: firstProductCode
    };

    function shouldScroll(): boolean {
      return (
        window.globalLister.scrollYToFirstProduct !== undefined && firstProductCode !== memo.current.lockedProductCode
      );
    }
  }, [firstProductCode]);
}

function getPageHeight(): number {
  const { scrollHeight } = window.document.querySelector('html') as HTMLElement;
  return scrollHeight;
}

function scrollToLockedProduct(pageHeight: number, previousPageHeight: number, isFirstPage: boolean): void {
  const topAdjustment = isFirstPage ? computeTopAdjustment() : 0;
  const scrollPosition =
    pageHeight - previousPageHeight + (window.globalLister.scrollYToFirstProduct ?? 0) - topAdjustment;
  window.scrollTo(0, scrollPosition);
  window.globalLister.scrollYToFirstProduct = undefined;
}

function computeTopAdjustment(): number {
  const showPrevButtonHeight = 40;
  const showPrevButtonVertMargin = 20 + 20;
  return showPrevButtonHeight + showPrevButtonVertMargin;
}

export default useStickyProductsPrepend;
